import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { getAuth } from 'firebase/auth';
import { useDemoStore } from '@/store/demoStore';
import { useUserStore } from '@/store/userStore';

// No-op component so router has a '/' match but renders nothing into #app.
// This lets the static landing in index.html remain visible.
import { onMounted, h } from 'vue';
const Noop = {
  name: 'Noop',
  setup() {
    onMounted(() => {
      document.dispatchEvent(new Event('render-event'));
    });
    return () => h('div');
  }
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'root',
    component: Noop,
    meta: { title: 'myuser — AI-Powered Marketing' }
  },
  {
    path: '/shopify',
    name: 'shopify-install',
    component: () => import('../views/ShopifyInstallView.vue'),
    meta: { title: 'Install — myuser' }
  },
  {
    path: '/demo',
    name: 'demo',
    component: () => import('../views/DemoView.vue'),
    meta: { requiresAuth: false, title: 'Demo — myuser' }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('../views/PrivacyView.vue'),
    meta: { title: 'Privacy Policy — myuser' }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('../views/TermsView.vue'),
    meta: { title: 'Terms of Service — myuser' }
  },
  {
    path: '/info',
    name: 'info',
    component: () => import('../views/InfoView.vue'),
    meta: { title: 'Company Info — myuser' }
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: { title: 'myuser — AI-Powered Marketing' }
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import('../views/BlogView.vue'),
    meta: { title: 'Blog — myuser' }
  },
  {
    path: '/blog/ai-email-generators',
    name: 'blog-ai-email-generators',
    component: () => import('../views/blog/blog_text/BlogAiEmail.vue'),
    meta: { title: 'AI Email Generators — myuser Blog' }
  },
  {
    path: '/blog/data-driven-email-campaigns',
    name: 'blog-data-driven',
    component: () => import('../views/blog/blog_text/BlogDataDriven.vue'),
    meta: { title: 'Data-Driven Email Campaigns — myuser Blog' }
  },
  {
    path: '/blog/cold-outreach-2025-personalization-at-scale',
    name: 'blog-cold-outreach',
    component: () => import('../views/blog/blog_text/BlogColdOutreach.vue'),
    meta: { title: 'Cold Outreach in 2025 — myuser Blog' }
  },
  {
    path: '/blog/email-personalization-ai',
    name: 'blog-email-personalization',
    component: () =>
      import('../views/blog/blog_text/BlogEmailPersonalization.vue'),
    meta: { title: 'Email Personalization with AI — myuser Blog' }
  },
  {
    path: '/blog/measuring-ai-email-roi',
    name: 'blog-measuring-ai-email-roi',
    component: () =>
      import('../views/blog/blog_text/BlogMeasuringAIEmailROI.vue'),
    meta: { title: 'Measuring AI Email ROI — myuser Blog' }
  },
  {
    path: '/blog/optimizing-subject-lines-with-ai',
    name: 'blog-optimizing-subject-lines',
    component: () =>
      import('../views/blog/blog_text/BlogOptimizingSubjectLines.vue'),
    meta: { title: 'Optimizing Subject Lines with AI — myuser Blog' }
  },
  {
    path: '/info-generation',
    name: 'infoGeneration',
    component: () => import('../views/InfoGenerationView.vue'),
    meta: { title: 'Generating Your Avatar — myuser' }
  },
  {
    path: '/slides',
    name: 'slides',
    component: () => import('@/views/SlidesView.vue'),
    meta: { title: 'Slides — myuser' }
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { title: 'Your Stores — myuser' }
  },
  {
    path: '/project-selection',
    name: 'projectSelection',
    component: () => import('@/views/ProjectSelectionView.vue'),
    meta: { title: 'Select a Store — myuser' }
  },
  {
    path: '/dashboard',
    redirect: '/dashboard/default'
  },
  {
    path: '/dashboard/:projectID',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    props: true,
    meta: { title: 'Dashboard — myuser' }
  },
  {
    path: '/user/:SimpleID/:projectID',
    name: 'user',
    component: () => import('@/views/UserView.vue'),
    props: true,
    meta: { title: 'Customer — myuser' }
  },
  {
    path: '/customers/:projectID',
    name: 'customers',
    component: () => import('../views/CustomersView.vue'),
    props: true,
    meta: { title: 'Customers — myuser' }
  },
  {
    path: '/info-chat',
    name: 'info-chat',
    component: () => import('../views/InfoChatView.vue'),
    meta: { title: 'AI Chat — myuser' }
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('@/views/AuthView.vue'),
    meta: { title: 'Sign In — myuser' }
  },
  {
    path: '/create-organization',
    name: 'create-organization',
    component: () => import('@/views/OrganizationCreationView.vue'),
    meta: { title: 'Create Organization — myuser' }
  },
  {
    path: '/account/:projectID',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    props: true,
    meta: { title: 'Account — myuser' }
  },
  {
    path: '/integrations/:projectID',
    alias: '/api/:projectID',
    name: 'integrations',
    component: () => import('@/views/IntegrationsView.vue'),
    props: true,
    meta: { title: 'Integrations — myuser' }
  },
  {
    path: '/organization/:projectID',
    name: 'organization',
    component: () => import('@/views/OrganizationView.vue'),
    props: true,
    meta: { title: 'Team — myuser' }
  },
  {
    path: '/organization/:projectID/invite',
    name: 'organization-invite',
    component: () => import('@/views/OrganizationView.vue'),
    props: true,
    meta: { title: 'Team Invite — myuser' }
  },
  {
    path: '/organization-settings/:projectID',
    name: 'organization-settings',
    component: () => import('@/views/OrganizationSettingsView.vue'),
    props: true,
    meta: { title: 'Settings — myuser' }
  },
  {
    path: '/sms/:projectID',
    name: 'sms',
    component: () => import('@/views/SMSView.vue'),
    props: true,
    meta: { title: 'SMS — myuser' }
  },
  {
    path: '/email/:projectID',
    name: 'email',
    component: () => import('../views/EmailView.vue'),
    props: true,
    meta: { title: 'Email — myuser' }
  },
  {
    path: '/templates/:projectID',
    name: 'templates',
    component: () => import('../views/TemplatesView.vue'),
    props: true,
    meta: { title: 'Templates — myuser' }
  },
  {
    path: '/catalog/:projectID',
    alias: '/marketing/:projectID',
    name: 'catalog',
    component: () => import('../views/MarketingView.vue'),
    props: true,
    meta: { title: 'Catalog — myuser' }
  },
  {
    path: '/forms/:projectID/:popupID?',
    name: 'forms',
    component: () => import('../views/FormsView.vue'),
    props: true,
    meta: { title: 'Forms — myuser' }
  },
  {
    path: '/more/:projectID',
    name: 'more',
    component: () => import('../views/MoreView.vue'),
    props: true,
    meta: { title: 'More — myuser' }
  },
  {
    path: '/branding/:projectID',
    alias: '/info/:projectID',
    name: 'branding',
    component: () => import('../views/CompanyInfoView.vue'),
    props: true,
    meta: { title: 'Branding — myuser' }
  },
  {
    path: '/p1/auth',
    name: 'p1auth',
    component: () => import('@/views/P1AuthView.vue'),
    meta: { title: 'Sign In — myuser' }
  },
  {
    path: '/p1/create-templates',
    name: 'p1createTemplates',
    component: () => import('@/views/P1CreatingTemplatesView.vue'),
    meta: { title: 'Create Templates — myuser' }
  },
  {
    path: '/p1/templates-view/:projectID',
    name: 'templatesView',
    component: () => import('@/views/P1ProjectPageView.vue'),
    meta: { title: 'Templates — myuser' }
  },
  {
    path: '/p1/templates-view/:projectID/invite',
    name: 'templatesInviteView',
    component: () => import('@/views/P1ProjectPageView.vue'),
    meta: { title: 'Templates — myuser' }
  },
  {
    path: '/approve-access/:projectID',
    name: 'approveAccess',
    component: () => import('@/views/ApproveAccessView.vue'),
    meta: { title: 'Access Request — myuser' }
  },
  {
    path: '/p1/create-templates/new',
    name: 'p1createTemplatesNew',
    component: () => import('@/views/P1CreatingTemplatesView.vue'),
    meta: { title: 'Create Templates — myuser' }
  },
  {
    path: '/p1/account/:projectID',
    name: 'p1Account',
    component: () => import('@/views/P1AccountView.vue'),
    meta: { title: 'Account — myuser' }
  },
  {
    path: '/p1/project-selection',
    name: 'p1ProjectSelection',
    component: () => import('@/views/P1ProjectSelectionView.vue'),
    meta: { title: 'Select Project — myuser' }
  },
  /*
  {
    path: '/p1/:pathMatch(.*)*',
    redirect: '/p1/create-templates/new'
  },
*/
  // ────────────────────────────────────────────────────────────────
  // New route for Stripe "success" redirect:
  {
    path: '/p1/subscription/success',
    name: 'subscriptionSuccess',
    component: () => import('@/views/SubscriptionSuccess.vue'),
    meta: { title: 'Subscription Activated — myuser' }
  }
  // ────────────────────────────────────────────────────────────────
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.afterEach((to) => {
  const title = to.meta?.title as string | undefined;
  if (title) {
    document.title = title;
  }
});

// Clear stale demo state when a real user navigates to a non-demo route
router.beforeEach((to, _from, next) => {
  const demoStore = useDemoStore();
  if (demoStore.isDemo && to.name !== 'demo') {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;
    const demoId = demoStore.demoTenantId;
    // If a real Firebase user is logged in, or the route doesn't match the demo tenant
    const routeProjectId = to.params.projectID as string | undefined;
    if (firebaseUser || (routeProjectId && routeProjectId !== demoId)) {
      console.warn(
        '[router] Clearing stale demo state on navigation to',
        to.path
      );
      demoStore.clearDemo();
      // Re-set Firebase token if user is logged in (clearDemo wipes userStore)
      if (firebaseUser) {
        firebaseUser.getIdToken().then((token) => {
          useUserStore().idToken = token;
        });
      }
    }
  }
  next();
});

export default router;
